import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

// Output types
export type ApiKeyListOutput = OutputRouter['apiKeys']['list'];
export type ApiKey = ApiKeyListOutput['apiKeys'][number];

// Input types
export type ApiKeyListInput = InputRouter['apiKeys']['list'];
export type ApiKeyCreateInput = InputRouter['apiKeys']['create'];
export type ApiKeyUpdateInput = InputRouter['apiKeys']['update'];
export type ApiKeyDeleteInput = InputRouter['apiKeys']['delete'];
export type ApiKeyRevokeInput = InputRouter['apiKeys']['revoke'];

/**
 * Hook to list API keys with pagination and sorting
 */
export function useApiKeysList(params: ApiKeyListInput) {
    return trpc.apiKeys.list.useQuery(params);
}

/**
 * Hook to create an API key
 */
export function useCreateApiKey() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.apiKeys.create.useMutation({
        onSuccess: () => {
            utils.apiKeys.list.invalidate();
            toast({
                title: 'Success',
                description: 'Access token created successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create access token',
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to update an API key
 */
export function useUpdateApiKey() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.apiKeys.update.useMutation({
        onSuccess: () => {
            utils.apiKeys.list.invalidate();
            toast({
                title: 'Success',
                description: 'Access token updated successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update access token',
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to delete API key(s)
 */
export function useDeleteApiKeys() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.apiKeys.delete.useMutation({
        onSuccess: () => {
            utils.apiKeys.list.invalidate();
            toast({
                title: 'Success',
                description: 'Access token(s) deleted successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete access token(s)',
                variant: 'destructive',
            });
        },
    });
}

/**
 * Hook to revoke an API key
 */
export function useRevokeApiKey() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.apiKeys.revoke.useMutation({
        onSuccess: () => {
            utils.apiKeys.list.invalidate();
            toast({
                title: 'Success',
                description: 'Access token revoked successfully',
            });
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to revoke access token',
                variant: 'destructive',
            });
        },
    });
}
